/**
 * Kalender: a resource calendar (owner, 2026-09-21). Names stay on the left and the days on top
 * while the grid scrolls; each absence is one block across its days, coloured by its category,
 * and a request not yet answered is dashed. A block opens the drawer; an empty day starts
 * Registrer fravær for that person and date, for somebody who may register one.
 */
import type { ApiAbsence, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { Button, Empty } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { PersonLabel } from '@/ui/PersonLabel';
import { DN, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { dow } from '@timefly/core';
import { useEffect, useRef, useState } from 'react';
import { VIEWS, type View, blocksFor, daysFor, stepFor, weeksOf } from './calendar';
import { CATEGORIES, catInfo, catOf } from './category';
import { periodText, spanOf } from './dates';
import { type Filter, personMatches, teamsOf } from './filters';
import { CatIcon, FilterBar } from './parts';

export function Kalender({
  all,
  users,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  tenantId,
  mayRegister,
  onOpen,
  onNew,
}: {
  all: ApiAbsence[];
  users: ApiUser[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  tenantId: number | undefined;
  mayRegister: boolean;
  onOpen: (a: ApiAbsence) => void;
  onNew: (uid: number, day: string) => void;
}) {
  const [view, setView] = useState<View>('uker');
  const [anchor, setAnchor] = useState(today);
  const days = daysFor(view, anchor);
  const weeks = weeksOf(days);
  const people = users.filter((u) => personMatches(u, filter, tenantId));
  const shown = filter.cat ? all.filter((a) => catOf(a.absence_type) === filter.cat) : all;
  const first = days[0] ?? today;
  const last = days[days.length - 1] ?? today;
  // a phone shows a week or so: start with today's column in view, next to the names
  const gridRef = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the days on screen change; it reads the DOM they rendered
  useEffect(() => {
    const el = gridRef.current;
    const now = el?.querySelector<HTMLElement>('.rday.now');
    const names = el?.querySelector<HTMLElement>('.rname');
    if (el && now && names) el.scrollLeft = Math.max(0, now.offsetLeft - names.offsetWidth - 8);
  }, [first, view]);

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        placeholder={T('Søk ansatt eller team')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      >
        <fieldset className="seg" aria-label={T('Visning')}>
          {VIEWS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>
              {T(label)}
            </button>
          ))}
        </fieldset>
        <PeriodControl
          period={view === 'maned' ? 'month' : 'three'}
          value={anchor}
          onChange={setAnchor}
          prevLabel={T('Forrige')}
          nextLabel={T('Neste')}
          onPrev={() => setAnchor(stepFor(view, anchor, -1))}
          onNext={() => setAnchor(stepFor(view, anchor, 1))}
        >
          <span className="lbl">{periodText(first, last)}</span>
        </PeriodControl>
        <Button onClick={() => setAnchor(today)} disabled={today >= first && today <= last}>
          {T('I dag')}
        </Button>
      </FilterBar>
      {people.length === 0 ? (
        <div className="mcard">
          <Empty>{T('Ingen ansatte passer filteret.')}</Empty>
        </div>
      ) : (
        <div className="mcard rcalw">
          <section
            ref={gridRef}
            className="rcal"
            data-testid="strip"
            style={{ '--n': days.length } as React.CSSProperties}
            aria-label={T('Fraværskalender')}
            // UI01-20: a day reached by keyboard clears the sticky names in both directions. The browser's
            // own focus scroll left Shift+Tab's cell 14 px under them; a click is never moved
            onFocus={(e) => {
              const el = e.target as HTMLElement;
              if (!el.matches?.(':focus-visible') || el.closest('.rname')) return;
              el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
              const sc = e.currentTarget;
              const hidden =
                sc.getBoundingClientRect().left +
                (Number.parseFloat(getComputedStyle(sc).scrollPaddingLeft) || 0) -
                el.getBoundingClientRect().left;
              if (hidden > 0) sc.scrollLeft -= hidden;
            }}
          >
            <div className="rrow rhead">
              <span className="rname rcorner">{T('Ansatt')}</span>
              {weeks.map((w) => (
                <span
                  key={w.week}
                  className="rweek"
                  style={{
                    gridColumn: `${w.start + 2} / span ${w.span}`,
                    gridRow: 1,
                  }}
                >
                  {T('Uke')} {w.week}
                </span>
              ))}
              {days.map((d, i) => (
                <span
                  key={d}
                  className={`rday${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                  style={{ gridColumn: i + 2, gridRow: 2 }}
                >
                  {DN[dow(d)]}
                  <b>{Number(d.slice(8, 10))}</b>
                </span>
              ))}
            </div>
            {people.map((u) => {
              const name = fullName(u);
              return (
                <div className="rrow" key={u.id} data-testid="cal-row">
                  <span className="rname" title={name}>
                    <PersonLabel id={u.id} name={name} />
                    <small>
                      {teamsOf(u, tenantId)
                        .map((t) => t.name)
                        .join(', ')}
                    </small>
                  </span>
                  {days.map((d, i) =>
                    mayRegister ? (
                      <button
                        key={d}
                        type="button"
                        className={`rcell${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                        style={{ gridColumn: i + 2 }}
                        aria-label={`${T('Registrer fravær')} ${name} ${periodText(d, d)}`}
                        onClick={() => onNew(u.id, d)}
                      />
                    ) : (
                      // registering for somebody is the administrator's; a manager's empty day is only a day
                      <span
                        key={d}
                        className={`rcell${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                        style={{ gridColumn: i + 2 }}
                      />
                    ),
                  )}
                  {blocksFor(shown, u.id, days).map((b) => {
                    const c = catOf(b.a.absence_type);
                    const { from, to } = spanOf(b.a);
                    const pend = b.a.status === 'pending';
                    const label = `${name}, ${b.a.absence_type?.name ?? T('Fravær')} ${periodText(from, to, false)}${pend ? `, ${T('venter på svar')}` : ''}`;
                    return (
                      <button
                        key={b.a.id}
                        type="button"
                        className={`rblk tn-${catInfo(c).tone}${pend ? ' pend' : ''}${b.cutL ? ' cl' : ''}${b.cutR ? ' cr' : ''}${b.end - b.start < 2 ? ' nar' : ''}`}
                        style={{ gridColumn: `${b.start + 2} / ${b.end + 3}` }}
                        title={label}
                        aria-label={label}
                        data-testid="cal-block"
                        onClick={() => onOpen(b.a)}
                      >
                        <Icon name={pend ? 'clock' : catInfo(c).icon} />
                        <span>
                          <b>{pend ? T('Venter på svar') : (b.a.absence_type?.name ?? T('Fravær'))}</b>
                          <small>{periodText(from, to, false)}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </section>
        </div>
      )}
      <div className="akey">
        {CATEGORIES.map((c) => (
          <span key={c.key}>
            <CatIcon cat={c.key} />
            {T(c.label)}
          </span>
        ))}
        <span>
          <i className="kpend" />
          {T('Venter på svar')}
        </span>
      </div>
    </>
  );
}
